// import { EnterOutlined } from "@ant-design/icons";
import { type Editor, isNodeSelection } from "@tiptap/react";
import { Button, ButtonProps, Popover } from "antd-v5";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { ToolBarButton } from "ac-components/ac-comment-editor/common";
import ExternalIcon from "ac-components/ac-icons/ExternalIcon";
import LinkIcon from "ac-components/ac-icons/LinkIcon";
import ReturnIcon from "ac-components/ac-icons/ReturnIcon";
import TrashIcon from "ac-components/ac-icons/TrashIcon";

// --- Styles ---
import "./link-popover.less";

export interface LinkHandlerProps {
  editor: Editor | null;
  onSetLink?: () => void;
}

export interface LinkMainProps {
  url: string;
  setUrl: React.Dispatch<React.SetStateAction<string>>;
  setLink: () => void;
  removeLink: () => void;
  isActive: boolean;
}

export const useLinkHandler = (props: LinkHandlerProps) => {
  const { editor, onSetLink } = props;
  const { href } = editor?.getAttributes("link") || {};
  const [url, setUrl] = useState<string>(href || "");

  useEffect(() => {
    if (!editor) return;

    const updateLinkState = () => {
      const { href } = editor.getAttributes("link");
      setUrl(href || "");
    };

    editor.on("selectionUpdate", updateLinkState);
    return () => {
      editor.off("selectionUpdate", updateLinkState);
    };
  }, [editor, url]);

  const setLink = useCallback(() => {
    if (!editor) return;

    const { from, to } = editor.state.selection;
    const text = editor.state.doc.textBetween(from, to);
    const linkText = text || url;

    if (!url) {
      editor.chain().extendMarkRange("link").unsetLink().run();
    } else {
      // Insert the link and place cursor after it in one transaction
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .insertContent({
          type: "text",
          text: linkText,
          marks: [{ type: "link", attrs: { href: url } }],
        })
        // This explicit unsetMark call is critical - it ensures we exit "link mode"
        .unsetMark("link")
        // Insert a zero-width space to ensure cursor is outside link context
        .insertContent(" ")
        .run();
    }

    onSetLink?.();
  }, [editor, onSetLink, url]);

  const removeLink = useCallback(() => {
    if (!editor) return;
    editor
      .chain()
      .focus()
      .unsetMark("link", { extendEmptyMarkRange: true })
      .setMeta("preventAutolink", true)
      .run();
    setUrl("");
  }, [editor]);

  return {
    url,
    setUrl,
    setLink,
    removeLink,
    isActive: editor?.isActive("link") || false,
  };
};

export const LinkButton = forwardRef<
  HTMLButtonElement,
  ButtonProps & { editor: Editor | null }
>(({ children, editor, ...props }, ref) => {
  return (
    <ToolBarButton
      role="button"
      tabIndex={-1}
      active={editor?.isActive("link")}
      aria-label="Link"
      title="Link"
      {...props}
    >
      {children || <LinkIcon />}
    </ToolBarButton>
  );
});

export const LinkContent: React.FC<{
  editor?: Editor | null;
}> = ({ editor }) => {
  const linkHandler = useLinkHandler({
    editor: editor!,
  });

  return <LinkMain {...linkHandler} />;
};

const LinkMain: React.FC<LinkMainProps> = ({
  url,
  setUrl,
  setLink,
  removeLink,
  isActive,
}) => {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      setLink();
    }
  };

  return (
    <>
      <input
        type="url"
        placeholder="Paste a link..."
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        className="tiptap-input tiptap-input-clamp"
      />

      <div className="tiptap-button-group">
        <Button onClick={setLink} title="Apply link" type="text">
          <ReturnIcon />
        </Button>

        <Button
          onClick={() => window.open(url, "_blank")}
          title="Open in new window"
          disabled={!url}
          type="text"
        >
          <ExternalIcon />
        </Button>

        <Button type="text" onClick={removeLink} title="Remove link">
          <TrashIcon />
        </Button>
      </div>
    </>
  );
};

export interface LinkPopoverProps extends Omit<ButtonProps, "type"> {
  /**
   * The TipTap editor instance.
   */
  editor?: Editor | null;
  /**
   * Whether to hide the link popover.
   * @default false
   */
  hideWhenUnavailable?: boolean;
  /**
   * Callback for when the popover opens or closes.
   */
  onOpenChange?: (isOpen: boolean) => void;
}

export function LinkPopover({
  editor,
  hideWhenUnavailable = false,
  onOpenChange,
  ...props
}: LinkPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);

  const onSetLink = () => {
    setIsOpen(false);
  };

  const linkHandler = useLinkHandler({
    editor: editor!,
    onSetLink,
  });

  const isDisabled = useMemo(() => {
    if (!editor) return true;
    if (editor.isActive("codeBlock")) return true;
    return !editor.can().setLink?.({ href: "" });
  }, [editor]);

  const isSelectionEmpty = () => {
    if (!editor) return true;
    const { selection } = editor.view.state;
    return selection.empty;
  };

  const canSetLink = useMemo(() => {
    if (!editor) return false;
    try {
      return editor.can().setMark("link");
    } catch {
      return false;
    }
  }, [editor]);

  const isActive = editor?.isActive("link") ?? false;

  const handleOnOpenChange = useCallback(
    (nextIsOpen: boolean) => {
      setIsOpen(nextIsOpen);
      onOpenChange?.(nextIsOpen);
    },
    [onOpenChange],
  );

  const show = useMemo(() => {
    if (!editor) {
      return false;
    }

    if (hideWhenUnavailable) {
      if (isNodeSelection(editor.state.selection) || !canSetLink) {
        return false;
      }
    }

    return true;
  }, [hideWhenUnavailable, editor, canSetLink]);

  if (!show || !editor || !editor.isEditable) {
    return null;
  }

  const disabled = isDisabled || isSelectionEmpty();

  return (
    <Popover
      trigger={"click"}
      open={!disabled && isOpen}
      onOpenChange={disabled ? () => {} : handleOnOpenChange}
      content={<LinkMain {...linkHandler} />}
    >
      <LinkButton
        disabled={disabled}
        data-active-state={isActive ? "on" : "off"}
        data-disabled={disabled}
        editor={editor}
        {...props}
      />
    </Popover>
  );
}

LinkButton.displayName = "LinkButton";
